import { FecThirdPartyRole } from '../enums/fecThirdPartyRole'
import { FecThirdPartySortKey } from '../enums/fecThirdPartySortKey'
import { FecThirdPartyStatus } from '../enums/fecThirdPartyStatus'
import type { FecThirdPartyMapping } from '../types/fecThirdPartyMapping'

const ROLE_WEIGHTS: Record<FecThirdPartyRole, number> = {
  [FecThirdPartyRole.Supplier]: 0,
  [FecThirdPartyRole.Client]: 1,
}

const STATUS_WEIGHTS: Record<FecThirdPartyStatus, number> = {
  [FecThirdPartyStatus.Existing]: 0,
  [FecThirdPartyStatus.New]: 1,
}

export interface FecThirdPartySort {
  isDescending: boolean
  key: FecThirdPartySortKey
}

export type FecThirdPartySortDirection = 'ascending' | 'descending' | false

interface SortFecThirdPartyMappingsParams {
  hasStatus: boolean
  isDescending: boolean
  sortKey: FecThirdPartySortKey
  thirdPartyMappings: FecThirdPartyMapping[]
}

const compareAccountNo = (left: FecThirdPartyMapping, right: FecThirdPartyMapping): number =>
  left.accountNo.localeCompare(right.accountNo, undefined, { numeric: true })

// The Type column shows the status tag when the status is shown, so it sorts by what the user sees there.
const getTypeWeight = (mapping: FecThirdPartyMapping, hasStatus: boolean): number =>
  hasStatus && mapping.status
    ? STATUS_WEIGHTS[mapping.status] * 2 + ROLE_WEIGHTS[mapping.role]
    : ROLE_WEIGHTS[mapping.role]

export const sortFecThirdPartyMappings = ({
  hasStatus,
  isDescending,
  sortKey,
  thirdPartyMappings,
}: SortFecThirdPartyMappingsParams): FecThirdPartyMapping[] => {
  const directionFactor = isDescending ? -1 : 1

  return [...thirdPartyMappings].sort((left, right) => {
    if (sortKey === FecThirdPartySortKey.Type) {
      const typeComparison = getTypeWeight(left, hasStatus) - getTypeWeight(right, hasStatus)

      if (typeComparison !== 0) {
        return typeComparison * directionFactor
      }

      return compareAccountNo(left, right)
    }

    return compareAccountNo(left, right) * directionFactor
  })
}

export const toggleFecThirdPartySort = (
  currentSort: FecThirdPartySort,
  key: FecThirdPartySortKey,
): FecThirdPartySort => {
  if (currentSort.key === key) {
    return { ...currentSort, isDescending: !currentSort.isDescending }
  }

  return { isDescending: false, key }
}

export const getFecThirdPartySortDirection = (
  sort: FecThirdPartySort,
  key: FecThirdPartySortKey,
): FecThirdPartySortDirection => {
  if (sort.key !== key) {
    return false
  }

  return sort.isDescending ? 'descending' : 'ascending'
}
